import {
  FormGroup,
  InputGroup,
  Intent,
  Switch,
  TextArea,
} from "@blueprintjs/core";
import { useEffect, useState, type ChangeEvent } from "react";
import type { Organization } from "../../types/phonebook";
import { PhonebookFormSection } from "./PhonebookFormSection";

interface PhonebookFormProps {
  content: Organization;
  onUpdate: (data: Organization) => void;
  onValidityChange?: (isValid: boolean) => void;
  canEditRestrictedOrgFields?: boolean;
  readOnly?: boolean;
}

export const PhonebookOrganizationForm = ({
  content,
  onUpdate,
  onValidityChange,
  canEditRestrictedOrgFields = false,
  readOnly = true,
}: PhonebookFormProps) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const canChangeRestrictedOrgFields = !readOnly && canEditRestrictedOrgFields;

  const validate = (data: Organization) => {
    const newErrors: Record<string, string> = {};

    if (!data.name || data.name.trim().length < 3) {
      newErrors.name = "Name must be at least 3 characters";
    }

    return newErrors;
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    const newContent = { ...content, [name]: value };
    onUpdate(newContent);
    validate(newContent);
  };

  const handleMemberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canChangeRestrictedOrgFields) return;

    const newContent = { ...content, member: e.target.checked };
    onUpdate(newContent);
    validate(newContent);
  };

  useEffect(() => {
    const errs = validate(content);
    setErrors(errs);
    onValidityChange?.(Object.keys(errs).length === 0);
  }, [content, onValidityChange]);

  return (
    <div className="focusoc-form-shell max-h-[72vh] w-[640px] max-w-[calc(100vw-4rem)] overflow-y-auto pr-1">
      <PhonebookFormSection
        eyebrow={readOnly ? "Read only" : "Editable"}
        rightContent={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {content?.id && (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold uppercase text-slate-600 ring-1 ring-inset ring-slate-200">
                {content.id}
              </span>
            )}
          </div>
        }
        title="Organization"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormGroup
            helperText={errors.name}
            intent={errors.name ? Intent.DANGER : Intent.NONE}
            label="Organization name"
            labelFor="organization-name-input"
          >
            <InputGroup
              id="organization-name-input"
              intent={errors.name ? Intent.DANGER : Intent.NONE}
              name="name"
              onChange={handleChange}
              placeholder="Organization name"
              readOnly={readOnly}
              value={content?.name || ""}
            />
          </FormGroup>

          <FormGroup label="Organization role" labelFor="org-role-input">
            <InputGroup
              id="org-role-input"
              name="orgRole"
              onChange={handleChange}
              placeholder="Organization role"
              readOnly={readOnly}
              value={content?.orgRole || ""}
            />
          </FormGroup>

          {/* SDA Member */}
          <FormGroup label="SDA Member" labelFor="member-input">
            <Switch
              id="member-input"
              checked={!!content?.member}
              disabled={!canChangeRestrictedOrgFields}
              onChange={handleMemberChange}
            />
          </FormGroup>

          <FormGroup label="Address" labelFor="address-input">
            <InputGroup
              id="address-input"
              name="address"
              onChange={handleChange}
              placeholder="Address"
              readOnly={readOnly}
              value={content?.address || ""}
            />
          </FormGroup>

          <FormGroup
            helperText={errors.phoneNumber}
            intent={errors.phoneNumber ? Intent.DANGER : Intent.NONE}
            label="Phone number"
            labelFor="phone-number-input"
          >
            <InputGroup
              id="phone-number-input"
              intent={errors.phoneNumber ? Intent.DANGER : Intent.NONE}
              name="phoneNumber"
              onChange={handleChange}
              placeholder="Phone number"
              readOnly={readOnly}
              value={content?.phoneNumber || ""}
            />
          </FormGroup>

          <FormGroup
            helperText={errors.email}
            intent={errors.email ? Intent.DANGER : Intent.NONE}
            label="Email"
            labelFor="email-input"
          >
            <InputGroup
              id="email-input"
              intent={errors.email ? Intent.DANGER : Intent.NONE}
              name="email"
              onChange={handleChange}
              placeholder="Email"
              readOnly={readOnly}
              value={content?.email || ""}
            />
          </FormGroup>

          <FormGroup
            className="md:col-span-2"
            label="Notes"
            labelFor="notes-input"
          >
            <TextArea
              className="min-h-[104px] resize-y !leading-relaxed"
              fill
              growVertically
              id="notes-input"
              name="notes"
              onChange={handleChange}
              placeholder="Notes"
              readOnly={readOnly}
              value={content?.notes || ""}
            />
          </FormGroup>
        </div>
      </PhonebookFormSection>
    </div>
  );
};
